import { Injectable } from '@angular/core';
import { ActivatedRouteSnapshot, CanActivate, Router, RouterStateSnapshot, UrlTree } from '@angular/router';
import { MsalBroadcastService, MsalService } from '@azure/msal-angular';
import { InteractionStatus } from '@azure/msal-browser';
import { Observable, of } from 'rxjs';
import { distinctUntilChanged, filter, switchMap } from 'rxjs/operators';
import { AccountService } from '../services/account.service';
import { HttpService } from '../services/http.service';
import { Group } from '../components/header/group-enum';

@Injectable({
  providedIn: 'root'
})
export class UserRoleGuard implements CanActivate {
  /**
   *
   */
  constructor(private accountService: AccountService, private httpService: HttpService, private router: Router,
    private msalBroadcastService: MsalBroadcastService, private authService: MsalService) {

  }

  canActivate(
    route: ActivatedRouteSnapshot,
    state: RouterStateSnapshot): Observable<boolean | UrlTree> | Promise<boolean | UrlTree> | boolean | UrlTree {
    return this.msalBroadcastService.inProgress$
      .pipe(
        filter((status: InteractionStatus) => status === InteractionStatus.None),
        distinctUntilChanged(),
        switchMap((status: InteractionStatus) => {
          if (this.accountService.getGroup().length > 0) {
            return of(this.validateGroups(route));
          }
          else {
            return this.checkUserAuthentication(route);
          }
        })
      );
  }

  /** check for user role authentication */
  checkUserAuthentication(route: ActivatedRouteSnapshot): Promise<boolean> {
    return new Promise((resolve) => {
      this.accountService.loadUser().then(() => {
        this.httpService.getUserRole().subscribe(
          (resp: any) => {
            if (resp?.data?.group) {
              const groups: Group[] = [];
              resp?.data?.group.forEach((groupString: string) => {
                if (this.getGroupEnumValue(groupString)) {
                  groups.push(this.getGroupEnumValue(groupString)!);
                }
              });

              this.accountService.setGroup(groups);
              this.accountService.setOEMs(resp?.data?.oems);
            }
            resolve(this.validateGroups(route));
          }
          , (error) => {
            this.authService.logout();
            resolve(false);
          });
      });
    });
  }

  private getGroupEnumValue(groupString: string): Group | null {
    if (groupString.includes("_Component-Admin")) {
      return Group.Component_Admin;
    }
    if (groupString.includes("_Component-Viewer")) {
      return Group.Component_Viewer;
    }
    if (groupString.includes("_Component-Support")) {
      return Group.Component_Support;
    }
    if (groupString.includes("_Configuration-Admin")) {
      return Group.Configuration_Admin;
    }
    if (groupString.includes("_Configuration-Viewer")) {
      return Group.Configuration_Viewer;
    }
    if (groupString.includes("_Software-Admin")) {
      return Group.Software_Admin;
    }
    if (groupString.includes("_Software-Viewer")) {
      return Group.Software_Viewer;
    }
    if (groupString.includes("_Bike-Admin")) {
      return Group.Bike_Admin;
    }
    if (groupString.includes("_Bike-Viewer")) {
      return Group.Bike_Viewer;
    }
    if (groupString.includes("_Bike-Support")) {
      return Group.Bike_Support;
    }
    if (groupString.includes("_Admin")) {
      return Group.Global_Admin;
    }
    if (groupString.includes("_Viewer")) {
      return Group.Global_Viewer;
    }
    return null;
  }

  validateGroups(route: ActivatedRouteSnapshot): boolean {
    const path = route.routeConfig?.path || "";

    if (!this.accountService.hasValidOEM()) {
      if (this.isLoginPath(path)) {
        return true;
      }
      else {
        this.router.navigate(["/permission-error"]);
      }
      
    }

    if (this.isComponentViewPath(path) && this.accountService.hasComponentViewRole()) {
      return true;
    }

    if (this.isComponentSupportPath(path) && this.accountService.hasComponentSupportRole()) {
      return true;
    }

    if (this.isComponentEditPath(path) && this.accountService.hasComponentEditRole()){
      return true;
    }

    if (this.isComponentSupportUploadPath(path) && this.accountService.hasComponentSetRecalledFlagRole()) {
      return true;
    }

    if (this.isConfigurationViewPath(path) && this.accountService.hasConfigurationViewRole()) {
      return true;
    }

    if (this.isConfigurationModifyPath(path) && this.accountService.hasConfigurationModifyRole()) {
      return true;
    }

    if (this.isSoftwareViewPath(path) && this.accountService.hasSoftwareViewRole()) {
      return true;
    }

    if (this.isSoftwareModifyPath(path) && this.accountService.hasSoftwareModifyRole()) {
      return true;
    }

    if (this.isBikeViewPath(path) && this.accountService.hasBikeViewRole()) {
      return true;
    }

    if (this.isUnassignedBikesViewPath(path) && this.accountService.hasUnassignedBikesViewRole()) {
      return true;
    }

    if (this.isBikeModifyPath(path) && this.accountService.hasBikeModifyRole()) {
      return true;
    }

    if (this.isBikeUnmapPath(path) && this.accountService.hasBikeUnmapRole()) {
      return true;
    }

    this.redirectToCorrectRoute(path);

    if (this.isLoginPath(path)) {
      return true;
    }

    return false;
  }

  redirectToCorrectRoute(path: string) {
    const groups = this.accountService.getGroup();

    if (groups.includes(Group.Component_Admin) || groups.includes(Group.Component_Viewer) || groups.includes(Group.Component_Support)) {
      this.router.navigate(["/components"]);
    }
    else if (groups.includes(Group.Configuration_Admin) || groups.includes(Group.Configuration_Viewer)) {
      this.router.navigate(["/configurations"]);
    }
    else if (groups.includes(Group.Software_Admin) || groups.includes(Group.Software_Viewer) || groups.includes(Group.Global_Admin) ||
      groups.includes(Group.Global_Viewer)) {
      this.router.navigate(["/software-overview"]);
    }
    else if (groups.includes(Group.Bike_Admin) || groups.includes(Group.Bike_Viewer) || groups.includes(Group.Bike_Support)) {
      this.router.navigate(["/bike-overview"]);
    }
    else if (path != "permission-error") {
      this.router.navigate(["/permission-error"]);
    }
  }

  public isComponentViewPath(path: string): boolean {
    if (path == "components") {
      return true;
    }
    return false;
  }

  public isComponentEditPath(path: string): boolean{
    if (path == "component-variant"){
      return true;
    }
    return false;
  }

  public isComponentSupportPath(path: string): boolean {
    if (path == "recalled-components") {
      return true;
    }
    return false;
  }

  public isComponentSupportUploadPath(path: string): boolean{
    if (path == "recalled-upload") {
      return true;
    }
    return false;
  }

  public isConfigurationViewPath(path: string): boolean {
    if (path == "configurations" || path == "view-configuration") {
      return true;
    }
    return false;
  }

  public isConfigurationModifyPath(path: string): boolean {
    if (path == "new-configuration" || path == "edit-configuration") {
      return true;
    }
    return false;
  }

  public isSoftwareViewPath(path: string): boolean {
    if (path == "view-software" || path == "software-overview" || path == "monitor-software" || path == "software-bikes-details"
      || path == "software-deliveries-details" || path == "software-success-details" || path == "software-errors-details") {
      return true;
    }
    return false;
  }

  public isSoftwareModifyPath(path: string): boolean {
    if (path == "view-software" || path == "new-software" || path == "edit-software" || path == "software-overview" || path == "start-approval-rollout"
      || path == "start-rollout" || path == "monitor-software" || path == "software-bikes-details" || path == "software-deliveries-details"
      || path == "software-success-details" || path == "software-errors-details") {
      return true;
    }
    return false;
  }

  public isBikeViewPath(path: string): boolean {
    if (path == "bike-overview" || path == "bike-details") {
      return true;
    }
    return false;
  }

  public isUnassignedBikesViewPath(path: string): boolean{
    if (path == "unassigned-bikes") {
      return true;
    }

    return false;
  }

  public isBikeModifyPath(path: string): boolean {
    if (path == "bike-overview" || path == "bike-details") {
      return true;
    }
    return false;
  }

  public isBikeUnmapPath(path: string): boolean {
    if (path == "bike-unmap") {
      return true;
    }
    return false;
  }

  public isLoginPath(path: string): boolean {
    if (path == "login" || path == "permission-error") {
      return true;
    }

    return false;
  }
}